import { useState, useEffect } from "react";
import api, { setEnrollToken } from "@/lib/api";
import { useAuth, apiError } from "@/context/AuthContext";
import { Loader2, ShieldCheck, Copy, Check, KeyRound } from "lucide-react";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";

function RecoveryCodes({ codes }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(codes.join("\n"));
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div data-testid="recovery-codes" className="mt-4">
      <div className="grid grid-cols-2 gap-2 p-3 rounded-lg bg-slate-50 border border-slate-200 font-mono text-sm">
        {codes.map((c) => <span key={c} className="tracking-wide">{c}</span>)}
      </div>
      <button type="button" onClick={copy} data-testid="copy-recovery-btn"
              className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-[#FC82ED] hover:underline">
        {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Copiati" : "Copia i codici"}
      </button>
    </div>
  );
}

// Blocking full-screen enrollment, used for mandatory roles (Direzione/Segreteria).
export function TwoFactorEnroll() {
  const { user, logout, patchUser } = useAuth();
  const [step, setStep] = useState("qr");
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.post("/auth/2fa/setup").then((r) => setSetup(r.data)).catch((e) => setErr(apiError(e)));
  }, []);

  const activate = async (e) => {
    e?.preventDefault(); setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/activate", { code: code.trim() });
      setRecovery(data.recovery_codes); setStep("codes");
    } catch (e) { setErr(apiError(e)); } finally { setLoading(false); }
  };

  const finish = () => { setEnrollToken(null); toast.success("2FA attivata. Accedi di nuovo per completare."); window.location.href = "/login"; };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6" data-testid="twofa-enroll-screen">
      <div className="w-full max-w-[420px]">
        <div className="mb-8"><Logo width={200} /></div>
        <div className="w-11 h-11 rounded-xl bg-[#FC82ED]/15 border border-[#FC82ED]/30 flex items-center justify-center text-[#FC82ED] mb-4">
          <ShieldCheck size={22} />
        </div>
        {step === "qr" && (
          <form onSubmit={activate}>
            <h2 className="font-display text-[1.6rem] font-bold tracking-tight mb-1.5">Attiva l'autenticazione a due fattori</h2>
            <p className="text-sm text-slate-500 mb-5">Il tuo ruolo richiede la 2FA. Scansiona il QR con Google Authenticator (o Authy) e inserisci il codice a 6 cifre, {user?.name?.split(" ")[0]}.</p>
            {setup ? (
              <>
                <div className="flex justify-center mb-3">
                  <img src={setup.qr} alt="QR 2FA" data-testid="twofa-qr" className="w-44 h-44 rounded-lg border border-slate-200" />
                </div>
                <p className="text-xs text-slate-400 text-center mb-4 break-all">Chiave manuale: <span className="font-mono">{setup.secret}</span></p>
                <input data-testid="twofa-code-input" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6} placeholder="Codice a 6 cifre"
                       className="w-full mb-4 px-3.5 py-2.5 rounded-lg bg-white border border-slate-200 focus:border-[#FC82ED] outline-none text-sm tracking-[0.3em] text-center" />
              </>
            ) : <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#FC82ED]" /></div>}
            {err && <div data-testid="twofa-error" className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
            <button data-testid="twofa-activate-btn" type="submit" disabled={loading || !setup}
                    className="w-full py-3 rounded-lg bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <Loader2 size={16} className="animate-spin" />} Verifica e attiva
            </button>
            <button type="button" onClick={logout} className="w-full mt-3 text-xs text-slate-400 hover:text-slate-600">Esci</button>
          </form>
        )}
        {step === "codes" && (
          <div>
            <h2 className="font-display text-[1.6rem] font-bold tracking-tight mb-1.5">Salva i codici di recupero</h2>
            <p className="text-sm text-slate-500">Conservali in un posto sicuro: ti permettono di accedere se perdi il telefono. Ogni codice è usabile una sola volta.</p>
            <RecoveryCodes codes={recovery} />
            <button data-testid="twofa-finish-btn" onClick={finish}
                    className="w-full mt-6 py-3 rounded-lg bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm transition-colors">
              Ho salvato i codici, continua
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Inline card for the Profile page: enable (optional for non-mandatory) or disable 2FA.
export function TwoFactorCard() {
  const { patchUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [mode, setMode] = useState(null); // null | 'setup' | 'codes' | 'disable'
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/auth/2fa/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    setErr(""); setLoading(true);
    try { const { data } = await api.post("/auth/2fa/setup"); setSetup(data); setMode("setup"); }
    catch (e) { setErr(apiError(e)); } finally { setLoading(false); }
  };
  const activate = async (e) => {
    e?.preventDefault(); setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/activate", { code: code.trim() });
      setRecovery(data.recovery_codes); setMode("codes"); setCode("");
      patchUser({ totp_enabled: true, mfa_enrollment_required: false });
    } catch (e) { setErr(apiError(e)); } finally { setLoading(false); }
  };
  const disable = async (e) => {
    e?.preventDefault(); setErr(""); setLoading(true);
    try {
      await api.post("/auth/2fa/disable", { code: code.trim() });
      setMode(null); setCode(""); patchUser({ totp_enabled: false }); await load();
      toast.success("2FA disattivata.");
    } catch (e) { setErr(apiError(e)); } finally { setLoading(false); }
  };

  if (!status) return null;

  return (
    <div className="card-immo p-5" data-testid="twofa-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#FC82ED]/15 border border-[#FC82ED]/30 flex items-center justify-center text-[#FC82ED] shrink-0"><KeyRound size={18} /></div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-sm">Autenticazione a due fattori (2FA)</div>
          <p className="text-sm text-slate-500 mt-0.5">
            {status.enabled ? `Attiva · ${status.recovery_remaining} codici di recupero rimasti` : "Non attiva"}
            {status.mandatory && <span className="ml-1 text-[#FC82ED] font-medium">· obbligatoria per il tuo ruolo</span>}
          </p>
        </div>
        {!mode && !status.enabled && (
          <button data-testid="twofa-enable-btn" onClick={startSetup} disabled={loading}
                  className="shrink-0 text-sm font-semibold px-3 py-1.5 rounded-lg bg-[#FC82ED] hover:bg-[#e06dd2] text-black transition-colors disabled:opacity-60">Attiva</button>
        )}
        {!mode && status.enabled && !status.mandatory && (
          <button data-testid="twofa-disable-open-btn" onClick={() => setMode("disable")}
                  className="shrink-0 text-sm font-medium px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:border-red-300 hover:text-red-600 transition-colors">Disattiva</button>
        )}
      </div>

      {mode === "setup" && setup && (
        <form onSubmit={activate} className="mt-4 border-t border-slate-100 pt-4">
          <div className="flex justify-center mb-3"><img src={setup.qr} alt="QR 2FA" data-testid="twofa-qr" className="w-40 h-40 rounded-lg border border-slate-200" /></div>
          <p className="text-xs text-slate-400 text-center mb-3 break-all">Chiave: <span className="font-mono">{setup.secret}</span></p>
          <input data-testid="twofa-code-input" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6} placeholder="Codice a 6 cifre"
                 className="w-full mb-3 px-3.5 py-2.5 rounded-lg bg-white border border-slate-200 focus:border-[#FC82ED] outline-none text-sm tracking-[0.3em] text-center" />
          {err && <div className="mb-3 text-sm text-red-600">{err}</div>}
          <div className="flex gap-2">
            <button data-testid="twofa-activate-btn" type="submit" disabled={loading} className="flex-1 py-2.5 rounded-lg bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm disabled:opacity-60">Verifica e attiva</button>
            <button type="button" onClick={() => { setMode(null); setErr(""); }} className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm">Annulla</button>
          </div>
        </form>
      )}

      {mode === "codes" && (
        <div className="mt-4 border-t border-slate-100 pt-4">
          <p className="text-sm text-slate-600">2FA attivata. Salva i tuoi codici di recupero (monouso):</p>
          <RecoveryCodes codes={recovery} />
          <button onClick={() => setMode(null)} className="w-full mt-4 py-2.5 rounded-lg bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm">Fatto</button>
        </div>
      )}

      {mode === "disable" && (
        <form onSubmit={disable} className="mt-4 border-t border-slate-100 pt-4">
          <p className="text-sm text-slate-600 mb-3">Inserisci un codice dell'app Authenticator per disattivare la 2FA.</p>
          <input data-testid="twofa-disable-code" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6} placeholder="Codice a 6 cifre"
                 className="w-full mb-3 px-3.5 py-2.5 rounded-lg bg-white border border-slate-200 focus:border-[#FC82ED] outline-none text-sm tracking-[0.3em] text-center" />
          {err && <div className="mb-3 text-sm text-red-600">{err}</div>}
          <div className="flex gap-2">
            <button data-testid="twofa-disable-btn" type="submit" disabled={loading} className="flex-1 py-2.5 rounded-lg bg-red-500 hover:bg-red-600 text-white font-semibold text-sm disabled:opacity-60">Disattiva 2FA</button>
            <button type="button" onClick={() => { setMode(null); setErr(""); }} className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm">Annulla</button>
          </div>
        </form>
      )}
    </div>
  );
}
